import { generateApolloClient } from "@/common/apolloClientUtil";
import { useAddressDialogNavigation } from "@/components/address-selector/composables/useAddressDialogNavigation";
import { useAddressSelectorCore } from "@/components/address-selector/composables/useAddressSelectorCore";
import {
  createMemberAddressProvider,
  type MemberAddressProviderContext,
} from "@/components/address-selector/providers/memberAddressProvider";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetPrefByChainDocument,
  type GetPrefByChainQuery,
  GetPrefCitySectionByZipCodeDocument,
  type GetPrefCitySectionByZipCodeQuery,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import { watch } from "vue";
import type { UseMemberRegisterAddressParams } from "../types";

/** 会員登録画面の住所選択ロジックを扱う composable。 */
export const useMemberRegisterAddress = (params: UseMemberRegisterAddressParams) => {
  const {
    errdialog,
    errMessage,
    createMember,
    addressDialog,
    areaDialog,
    zip_code,
    prefSelectValue,
    prefListItems,
    btnNonMemberToggle,
    errorZipCode,
    errorPrefSelect,
    userErrorMessage,
    clickMode,
    zipCodeFromPref,
    cityBlockList,
    sectionBlockList,
    blockList,
    originalAddress,
    originalCityAddress,
    originalSectionAddress,
    unassignedBlockList,
    wordList,
  } = params;

  const provider = createMemberAddressProvider();

  const providerContext: MemberAddressProviderContext = {
    createMember,
    wordList: wordList.value,
  };

  watch(
    wordList,
    (value) => {
      providerContext.wordList = value;
    },
    { deep: true }
  );

  const { state, clearErrors, clearAreaState, setDialog, searchByZip, loadCityListByPref, selectCity, selectSection, selectBlock } =
    useAddressSelectorCore<MemberAddressProviderContext>({
      provider,
      context: providerContext,
    });

  /** 画面stateをコアstateへ同期する。 */
  const syncCoreFromRefs = (): void => {
    state.zipCode = zip_code.value;
    state.pref = prefSelectValue.value;
    state.city = originalCityAddress.value;
    state.section = originalSectionAddress.value;
    state.block = "";
    state.cityBlockList = cityBlockList.value ?? null;
    state.sectionBlockList = sectionBlockList.value ?? null;
    state.blockList = (blockList.value ?? []).filter(
      (value): value is { name: string | null | undefined; enabled: boolean } =>
        value !== null && value !== undefined
    );
    state.errors.zip = errorZipCode.value;
    state.errors.pref = errorPrefSelect.value;
    state.errors.general = userErrorMessage.value;
    state.dialog.addressDialog = addressDialog.value;
    state.dialog.areaDialog = areaDialog.value;
  };

  /** コアstateを画面stateへ同期する。 */
  const syncRefsFromCore = (): void => {
    zip_code.value = state.zipCode;
    prefSelectValue.value = state.pref;
    cityBlockList.value = state.cityBlockList;
    sectionBlockList.value = state.sectionBlockList;
    blockList.value = state.blockList;
    errorZipCode.value = state.errors.zip;
    errorPrefSelect.value = state.errors.pref;
    userErrorMessage.value = state.errors.general;
    addressDialog.value = state.dialog.addressDialog;
    areaDialog.value = state.dialog.areaDialog;
    unassignedBlockList.value = state.blockList
      .filter((value) => !value.enabled)
      .map((value) => value.name || wordList.value.lbl_other_block_disp_name);
  };

  /** エリア選択状態を初期化する。 */
  const areaClear = (): void => {
    clearAreaState();
    state.city = "";
    state.section = "";
    originalCityAddress.value = "";
    originalSectionAddress.value = "";
    syncRefsFromCore();
  };

  // 住所ダイアログのクローズ/戻る分岐は共通フックへ委譲する。
  const { onCloseAddressDialog, onClickAreaDialogReturn } = useAddressDialogNavigation({
    state,
    btnNonMemberToggle,
    originalCityAddress,
    originalSectionAddress,
    unassignedBlockList,
    syncCoreFromRefs,
    syncRefsFromCore,
    setDialog,
    areaClear,
  });

  /** 住所選択画面に遷移する。 */
  const onClickAddressUpdate = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();

    const prefList: ApolloQueryResult<GetPrefByChainQuery> =
      await apolloClient.query<GetPrefByChainQuery>({
        query: GetPrefByChainDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
        },
        fetchPolicy: "no-cache",
      });

    prefListItems.value = prefList.data.getPrefByChain ?? [];

    // 未確定入力を保持するため、画面保持値を優先し、空の場合のみ確定済み住所を初期値に使う。
    if (!zip_code.value && createMember.zip_code) {
      zip_code.value = createMember.zip_code;
    }
    state.zipCode = zip_code.value;
    state.pref = prefSelectValue.value;
    setDialog(true, false);
    syncRefsFromCore();
  };

  /** 郵便番号入力イベントを処理する。 */
  const onZipCodeInput = async (value?: string): Promise<void> => {
    if (typeof value === "string") {
      zip_code.value = value;
    }

    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";

    if (zip_code.value.length !== 7) {
      return;
    }

    const apolloClient = await generateApolloClient();
    const result: ApolloQueryResult<GetPrefCitySectionByZipCodeQuery> =
      await apolloClient.query<GetPrefCitySectionByZipCodeQuery>({
        query: GetPrefCitySectionByZipCodeDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
          zip_code: zip_code.value,
        },
        fetchPolicy: "no-cache",
      });

    if (!result.data.getPrefCitySectionByZipCode || result.data.getPrefCitySectionByZipCode.length === 0) {
      errorZipCode.value = "入力した郵便番号はご利用対象外です";
    }
  };

  /** 郵便番号モードに切り替える。 */
  const onClickToggleZip = async (): Promise<void> => {
    clearErrors();
    btnNonMemberToggle.value = 0;
    syncCoreFromRefs();
    syncRefsFromCore();
  };

  /** 都道府県モードに切り替える。 */
  const onClickTogglePref = async (): Promise<void> => {
    clearErrors();
    btnNonMemberToggle.value = 1;
    syncCoreFromRefs();
    syncRefsFromCore();
  };

  /** 都道府県入力イベントを処理する。 */
  const onPrefSelectInput = async (): Promise<void> => {
    syncCoreFromRefs();
    state.pref = prefSelectValue.value;
    clearErrors();
    syncRefsFromCore();
  };

  /** 検索ボタン押下時の住所検索を処理する。 */
  const onClickDeliveryRequestAndNewMember = async (): Promise<void> => {
    if (clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      syncCoreFromRefs();
      clearErrors();
      areaClear();

      if (btnNonMemberToggle.value === 0) {
        // 検索ボタン押下時にも対象外チェックを再実行し、対象外なら遷移しない。
        await onZipCodeInput(zip_code.value);
        if (errorZipCode.value.length > 0) {
          return;
        }

        const resolved = await searchByZip();
        syncRefsFromCore();

        // 郵便番号検索でエリア選択へ進む場合の選択中住所表示を同期する。
        if (!resolved) {
          originalCityAddress.value = state.city;
          originalSectionAddress.value = state.section;
        }

        if (resolved) {
          zipCodeFromPref.value = resolved.zip_code;
          state.pref = resolved.pref_name ?? state.pref;
          state.city = resolved.city_name ?? state.city;
          state.section = resolved.section_name ?? state.section;
          prefSelectValue.value = state.pref;
          originalCityAddress.value = state.city;
          originalSectionAddress.value = state.section;
          syncRefsFromCore();
          return;
        }

        if (
          state.blockList.length === 0 &&
          state.cityBlockList === null &&
          state.sectionBlockList === null
        ) {
          // 配達可能な住所がない場合は住所選択ダイアログにエラーを表示する
          errorZipCode.value = "入力した郵便番号はご利用対象外です";
          return;
        }

        originalAddress.value = prefSelectValue.value;
        return;
      }

      if (btnNonMemberToggle.value === 1) {
        state.pref = prefSelectValue.value;
        await loadCityListByPref();
        originalAddress.value = prefSelectValue.value;
        syncRefsFromCore();
      }
    } finally {
      clickMode.value = false;
    }
  };

  /** 市区リンクボタン押下時を処理する。 */
  const onClickCityBlockLink = async (item: string | null | undefined): Promise<void> => {
    if (!item || clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      syncCoreFromRefs();
      await selectCity(item);
      originalCityAddress.value = item;
      originalSectionAddress.value = "";
      syncRefsFromCore();
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "町村の取得に失敗しました。";
      areaClear();
    } finally {
      clickMode.value = false;
    }
  };

  /** 町域リンクボタン押下時を処理する。 */
  const onClickSectionBlockLink = async (item: string | null | undefined): Promise<void> => {
    if (!item || clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      syncCoreFromRefs();
      const resolved = await selectSection(item);
      originalSectionAddress.value = item === "その他" ? "" : item;
      syncRefsFromCore();

      if (resolved) {
        zipCodeFromPref.value = resolved.zip_code;
        createMember.zip_code = resolved.zip_code;
        createMember.address1 = resolved.address1;
        areaDialog.value = false;
      }
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
      areaClear();
    } finally {
      clickMode.value = false;
    }
  };

  /** 丁目リンクボタン押下時を処理する。 */
  const onClickAddressBlockLink = async (item: string | null | undefined): Promise<void> => {
    if (!item || clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      syncCoreFromRefs();
      const resolved = await selectBlock(item);
      syncRefsFromCore();

      if (resolved) {
        zipCodeFromPref.value = resolved.zip_code;
        createMember.zip_code = resolved.zip_code;
        createMember.address1 = resolved.address1;
        areaDialog.value = false;
      }
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    } finally {
      clickMode.value = false;
    }
  };

  return {
    onClickAddressUpdate,
    onCloseAddressDialog,
    onClickAreaDialogReturn,
    onZipCodeInput,
    onClickToggleZip,
    onClickTogglePref,
    onPrefSelectInput,
    onClickDeliveryRequestAndNewMember,
    onClickCityBlockLink,
    onClickSectionBlockLink,
    onClickAddressBlockLink,
  };
};
